import type { PostFXSettings, SceneState } from "../../../shared/api-contract";
import { useDomainState, useMutate } from "../../../state/editor/useEditor";
import { CompactCheck, CompactSlider, InertChip } from "../inspector/rows";
import type { InertTag } from "../inspector/schema";


/**
 * Advanced scene panel — the leftover Scene-menu knobs that don't have a home
 * card yet: env rotation/intensity, fixed env direction, the tone-mapping pair,
 * and the tone-mapping pair.
 *
 * EVERYTHING here reads and writes the shared `scene` slice. The two sliders
 * used to be the exception — panel-local state seeded once from getSceneState()
 * — and that drifted both ways: an Env Intensity set in the Scene tab left this
 * slider showing the mount-time number, and dragging it then pushed the stale
 * value back over the tab's edit. Worse, the rotation slider re-sent its
 * mount-time x and z on every y-drag, silently discarding an x the tab had set.
 * `mutate()` patches the slice optimistically and rolls back if the RPC
 * rejects, so one value has one home. That rollback replaced this panel's own
 * error span: a failed write now routes through the store's central onError AND
 * snaps the control back to the truth, which is louder than a line of red text:
 *
 * - "Fixed Env Direction" is the boolean face of the Scene tab's "Env Follow
 *   Camera" slider and of the identically-labelled checkbox in Background /
 *   Environment (advanced); a seeded-once local copy left the three showing
 *   different answers.
 * - "Tonemap Background + Ground" drives BOTH post-FX flags from one control,
 *   off the `postFX` slice that the Scene tab's and Post Processing tab's rows
 *   render from. It used to be a lone `tonemapBackground` checkbox over a
 *   seeded-once local boolean, which made this the only place in the UI that
 *   could half-tonemap the frame (see the row's own comment).
 */
export function AdvancedPanel() {
  // The slice reads undefined until the store's eager seed lands, and this
  // panel can be the very first thing rendered (its open/closed state is
  // persisted). The engine boots world-locked, so 0 is the honest stand-in.
  const scene = useDomainState("scene") as SceneState | undefined;
  const fixedEnv = (scene?.envFollowCamera ?? 0) === 0;
  // Same deal for the tone-mapping pair: one shared slice, no local copy.
  // Undefined until the eager seed lands; both flags default off, which is also
  // the pipeline's default, so `false` is the honest stand-in.
  const postFX = useDomainState("postFX") as PostFXSettings | undefined;
  const tonemapBg = postFX?.tonemapBackground ?? false;
  const tonemapGround = postFX?.tonemapGround ?? false;
  const tonemapped = tonemapBg && tonemapGround;
  const tonemapMixed = tonemapBg !== tonemapGround;
  const mutate = useMutate();

  // Straight off the shared slice — no local copy to go stale. The defaults
  // only cover the first frame before the slice has loaded.
  const envRot = scene?.envRotation ?? { x: 0, y: 0, z: 0 };
  const envIntensity = scene?.envIntensity ?? 1;

  /**
   * The two env sliders below write straight to the viewer (the checkboxes go
   * through the store instead). A rejected setter used to escape as an
   * unhandled rejection, leaving the local state showing a value the viewer
   * never accepted; now the mismatch is at least visible.
   */

  return (
    <div className="flex flex-col gap-4 p-3">
      <Section title="Environment">
        <Row label="Env Rotation">
          <CompactSlider
            value={envRot.y}
            min={-Math.PI}
            max={Math.PI}
            step={0.01}
            onChange={(v) => {
              // x and z come from the SLICE, so a y-drag no longer re-sends
              // whatever they happened to be when this panel mounted.
              mutate("scene", {
                patch: { envRotation: { ...envRot, y: v } },
                call: (api) => Promise.resolve(api.setEnvRotation(envRot.x, v, envRot.z)),
              });
            }}
          />
        </Row>
        <Row label="Env Intensity">
          <CompactSlider
            value={envIntensity}
            min={0}
            max={5}
            step={0.01}
            onChange={(v) => {
              mutate("scene", {
                patch: { envIntensity: v },
                call: (api) => Promise.resolve(api.setEnvIntensity(v)),
              });
            }}
          />
        </Row>
        <Row label="Fixed Env Direction">
          <CompactCheck
            checked={fixedEnv}
            onChange={(v) => {
              const follow = v ? 0 : 1;
              // Through the store, not `apply`: the shared slice is what keeps
              // this checkbox, the Scene tab's slider and the advanced-section
              // checkbox showing the same value. mutate() already rolls back
              // (refreshes the slice) if the RPC rejects.
              mutate("scene", {
                patch: { envFollowCamera: follow },
                call: (api) => Promise.resolve(api.setEnvFollowCamera(follow)),
              });
            }}
          />
        </Row>
      </Section>

      <Section title="Rendering">
        {/* One control, both flags. The compositor tone-maps background and
            ground pixels independently, but the G-buffer stamps a ground proxy
            across the lower frame whenever the ground is on (the default), so
            moving one flag alone seams the frame at the horizon: the sky
            ACES-rolls to ~#e2e2e2 while the lower half stays as picked. The
            Post Processing tab still exposes them separately for anyone who
            wants that; this bucket panel does not. Through the store, not
            `apply`, so the Scene tab's and Post FX tab's copies of these rows
            follow along instead of going stale. */}
        <Row label="Tonemap Background + Ground">
          <CompactCheck
            checked={tonemapped}
            onChange={() => {
              // Nothing to toggle before the eager seed lands: the optimistic
              // patch would replace the whole (still-undefined) slice with a
              // two-key object and the other fx-bound rows read into it.
              if (!postFX) return;
              // From a mixed state the box reads unchecked, so the first click
              // turns BOTH on — either way the seam is gone afterwards.
              const next = !tonemapped;
              const patch = { tonemapBackground: next, tonemapGround: next };
              mutate("postFX", {
                patch,
                call: (api) => api.setPostFX(patch),
                reconcile: (res) => res,
              });
            }}
          />
        </Row>
        {tonemapMixed ? (
          <p className="text-[11px] leading-snug text-ed-orange">
            Post Processing has these two set differently, which seams the frame at the horizon.
            Ticking this aligns both.
          </p>
        ) : null}
      </Section>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-2">
      <span className="text-[12px] font-semibold text-ed-text-2">{title}</span>
      {children}
    </div>
  );
}

function Row({ label, children, inert }: { label: string; children: React.ReactNode; inert?: InertTag }) {
  return (
    <div className="flex items-center gap-2">
      <span className="flex w-[42%] shrink-0 items-center gap-1 text-[12px] text-ed-text-2">
        <span className="truncate">{label}</span>
        {inert ? <InertChip tag={inert} /> : null}
      </span>
      <div className="flex min-w-0 flex-1 items-center justify-end gap-2">{children}</div>
    </div>
  );
}
